import {
  Box,
  Button,
  Flex,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Spinner,
  Stack,
  Tag,
  TagCloseButton,
  Text,
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';

import { Search2 } from '@/client/components/icons/ContinuIcons';
import type { Skill } from '@/client/types/Skill';
import SkillService from '@/client/services/api/SkillService';
import type { UserDetailsFormData } from '@/client/components/learners/profile/overlay/LearnerProfileUserDetailsFormModal';
import { useAuthStore } from '@/client/services/state/authStore';
import { useDebounceValue } from 'usehooks-ts';
import { useFormContext } from 'react-hook-form';
import { useToastStore } from '@/client/services/state/toastStore';
import { useTranslation } from 'react-i18next';

export default function LearnerProfileSkillsForm() {
  const { t } = useTranslation();
  const { setToast } = useToastStore();
  const { authConfig } = useAuthStore();
  const { user } = authConfig;

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm] = useDebounceValue(searchTerm, 300);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const searchResultsRef = useRef<HTMLDivElement>(null);

  const { watch, setValue } = useFormContext<UserDetailsFormData>();

  const { skills } = watch();

  const { data, isFetching } = useQuery({
    enabled: !!debouncedSearchTerm,
    queryKey: ['skill-search', debouncedSearchTerm],
    queryFn: () => SkillService.searchSkills(debouncedSearchTerm, 'newest'),
  });

  const searchResults = data?.data || [];

  const createSkillsMutation = useMutation({
    mutationFn: (skillNames: string[]) => SkillService.createSkills(skillNames, user._id),
    onSuccess: (response) => {
      // Find the newly created skill and update the form
      const newlyCreatedSkill = response.data.find(
        (createdSkill: Skill) => createdSkill.name === debouncedSearchTerm,
      );

      if (newlyCreatedSkill) {
        const updatedSkills = [...skills, newlyCreatedSkill];
        setValue('skills', updatedSkills, { shouldDirty: true });
      }

      setSearchTerm('');
      setSelectedIndex(-1);
    },
    onError: () => {
      setToast({
        show: true,
        status: 'error',
        title: t('learners.profile.skills.create.error'),
      });
    },
  });

  const handleRemoveSkill = (skillId: string) => {
    const updatedSkills = skills.filter((skill) => skill._id !== skillId);

    setValue('skills', updatedSkills, { shouldDirty: true });
  };

  const addNewSkill = (skill: Skill) => {
    // If the skill doesn't have an _id, it's a new skill that needs to be created
    if (!skill._id) {
      createSkillsMutation.mutate([skill.name]);
    } else {
      // If it has an _id, it's an existing skill, just add it directly
      const updatedSkills = [...skills, skill];
      setValue('skills', updatedSkills, { shouldDirty: true });
    }
  };

  const onAddExistingSkill = (skill: Skill) => {
    // Check if skill already exists
    const skillExists = skills.some((existingSkill) => existingSkill._id === skill._id);

    if (skillExists) {
      setToast({
        show: true,
        status: 'error',
        title: t('learners.profile.skills.create.exists'),
      });
    }

    if (!skillExists) {
      const updatedSkills = [...skills, skill];
      setValue('skills', updatedSkills, { shouldDirty: true });
    }

    setSearchTerm('');
    setSelectedIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!searchResults.length) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => (prev < searchResults.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < searchResults.length) {
          onAddExistingSkill(searchResults[selectedIndex]);
        }
        break;
      case 'Escape':
        setSearchTerm('');
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };

  // Reset selected index when search results change
  useEffect(() => {
    setSelectedIndex(-1);
  }, [searchResults]);

  // Auto-scroll to keep selected item visible
  useEffect(() => {
    if (selectedIndex >= 0 && searchResultsRef.current) {
      const container = searchResultsRef.current;
      const selectedElement = container.children[selectedIndex] as HTMLElement;

      if (selectedElement) {
        const containerRect = container.getBoundingClientRect();
        const elementRect = selectedElement.getBoundingClientRect();

        // Check if element is outside the visible area
        if (elementRect.top < containerRect.top) {
          // Element is above the visible area, scroll up
          container.scrollTop -= containerRect.top - elementRect.top;
        } else if (elementRect.bottom > containerRect.bottom) {
          // Element is below the visible area, scroll down
          container.scrollTop += elementRect.bottom - containerRect.bottom;
        }
      }
    }
  }, [selectedIndex]);

  return (
    <Stack spacing={6}>
      <Text variant="formSectionHeading">{t('learners.profile.skills.title')}</Text>

      <Box position="relative">
        <Flex alignItems="center">
          <InputGroup>
            <InputLeftElement>
              <Search2 />
            </InputLeftElement>

            <Input
              variant="warmNeutral"
              placeholder="Search skills"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
            />

            <InputRightElement>
              {isFetching && <Spinner size="sm" color="warmNeutral.500" />}
            </InputRightElement>
          </InputGroup>

          {!isFetching && !!debouncedSearchTerm && (
            <Button
              size="md"
              paddingX={4}
              marginLeft={2}
              onClick={() => addNewSkill({ _id: '', name: debouncedSearchTerm })}
              isLoading={createSkillsMutation.isLoading}
              disabled={createSkillsMutation.isLoading}
            >
              {t('learners.profile.skills.add')}
            </Button>
          )}
        </Flex>

        {searchResults.length > 0 && (
          <Box
            ref={searchResultsRef}
            position="absolute"
            top={12}
            left={0}
            right={0}
            maxHeight="200px"
            overflowY="auto"
            border="1px"
            borderColor="warmNeutral.300"
            borderRadius="md"
            backgroundColor="white"
            zIndex={10}
          >
            {searchResults.map((skill: Skill, index: number) => (
              <Box
                key={skill._id}
                padding={3}
                cursor="pointer"
                backgroundColor={selectedIndex === index ? 'warmNeutral.0' : 'transparent'}
                _hover={{ backgroundColor: selectedIndex === index ? 'blue.50' : 'gray.50' }}
                onClick={() => onAddExistingSkill(skill)}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                <Text>{skill.name}</Text>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      <HStack flexWrap="wrap" gap={2}>
        {skills.map((skill) => (
          <Tag key={skill._id} variant="warmNeutral">
            {skill.name}

            <TagCloseButton onClick={() => handleRemoveSkill(skill._id)} />
          </Tag>
        ))}
      </HStack>
    </Stack>
  );
}
